import { useState, useEffect } from 'react';
import type { LoraModel, LoraCreateRequest } from '../types';
import { loraApi } from '../services/loraApi';
import './LoraManager.css';

interface LoraManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectModel: (modelId: string | null) => void;
  selectedModelId: string | null;
}

export function LoraManager({ isOpen, onClose, onSelectModel, selectedModelId }: LoraManagerProps) {
  const [models, setModels] = useState<LoraModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<LoraCreateRequest>({
    name: '',
    hf_repo_id: '',
    default_weight: 0.8,
    epoch: undefined,
    description: '',
    is_default: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen]);

  const fetchModels = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await loraApi.list();
      setModels(response.models);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load models');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await loraApi.create(formData);
      setFormData({
        name: '',
        hf_repo_id: '',
        default_weight: 0.8,
        epoch: undefined,
        description: '',
        is_default: false,
      });
      setShowAddForm(false);
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create model');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this model?')) return;

    try {
      await loraApi.delete(id);
      if (selectedModelId === id) {
        onSelectModel(null);
      }
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await loraApi.setDefault(id);
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lora-manager-overlay" onClick={onClose}>
      <div className="lora-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lora-manager-header">
          <h2>My Models</h2>
          <button className="close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="lora-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="lora-manager-content">
          {/* Base Model Option */}
          <div
            className={`lora-item base-model ${selectedModelId === null ? 'selected' : ''}`}
            onClick={() => onSelectModel(null)}
          >
            <div className="lora-info">
              <span className="lora-name">ACE-Step (Base)</span>
              <span className="lora-repo">Default model - no LoRA</span>
            </div>
            {selectedModelId === null && <span className="selected-badge">Active</span>}
          </div>

          {/* User's LoRA Models */}
          {isLoading ? (
            <div className="lora-loading">Loading models...</div>
          ) : models.length === 0 ? (
            <div className="lora-empty">
              <p>No custom models registered yet.</p>
              <p>Add your finetuned LoRA models from HuggingFace.</p>
            </div>
          ) : (
            models.map((model) => (
              <div
                key={model.id}
                className={`lora-item ${selectedModelId === model.id ? 'selected' : ''}`}
                onClick={() => onSelectModel(model.id)}
              >
                <div className="lora-info">
                  <div className="lora-name-row">
                    <span className="lora-name">{model.name}</span>
                    {model.is_default && <span className="default-badge">Default</span>}
                  </div>
                  <span className="lora-repo">{model.hf_repo_id}</span>
                  <span className="lora-meta">
                    Weight: {model.default_weight} {model.epoch !== null && `• Epoch ${model.epoch}`}
                  </span>
                </div>
                <div className="lora-actions">
                  {selectedModelId === model.id && <span className="selected-badge">Active</span>}
                  {!model.is_default && (
                    <button
                      className="lora-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(model.id);
                      }}
                      title="Set as default"
                    >
                      ★
                    </button>
                  )}
                  <button
                    className="lora-action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(model.id);
                    }}
                    title="Delete"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add New Model Section */}
        <div className="lora-add-section">
          {!showAddForm ? (
            <button className="add-model-btn" onClick={() => setShowAddForm(true)}>
              + Add LoRA Model
            </button>
          ) : (
            <form className="lora-add-form" onSubmit={handleSubmit}>
              <h3>Register New LoRA</h3>
              
              <div className="form-group">
                <label htmlFor="name">Display Name *</label>
                <input
                  id="name"
                  type="text"
                  placeholder="e.g., My Jazz Style"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="hf_repo_id">HuggingFace Repo ID *</label>
                <input
                  id="hf_repo_id"
                  type="text"
                  placeholder="e.g., username/model-name"
                  value={formData.hf_repo_id}
                  onChange={(e) => setFormData({ ...formData, hf_repo_id: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="default_weight">Default Weight</label>
                  <input
                    id="default_weight"
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.default_weight}
                    onChange={(e) => setFormData({ ...formData, default_weight: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="epoch">Epoch (optional)</label>
                  <input
                    id="epoch"
                    type="number"
                    min="0"
                    placeholder="e.g., 2"
                    value={formData.epoch ?? ''}
                    onChange={(e) => setFormData({ ...formData, epoch: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (optional)</label>
                <textarea
                  id="description"
                  placeholder="Describe your model style..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  Set as default model
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Model'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

